import React, { useState, useEffect, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import axiosInstance from "../../api/axiosInstance";
import { Link } from "react-router-dom";
import { useLogout } from "../../features/auth/Logout";
const Navbar = () => {
	const [menuOpen, setMenuOpen] = useState(false);
	const [computeDropdownOpen, setComputeDropdownOpen] = useState(false);
	const [storageDropdownOpen, setStorageDropdownOpen] = useState(false);
	const [networkDropdownOpen, setNetworkDropdownOpen] = useState(false);
	const [isImpersonated, setIsImpersonated] = useState(false);
	const logout = useLogout();
	const computeRef = useRef(null);
	const storageRef = useRef(null);
	const networkRef = useRef(null);
	const [darkMode, setDarkMode] = useState(() => {
		const savedTheme = localStorage.getItem("theme");
		if (savedTheme) return savedTheme === "dark";
		return window.matchMedia("(prefers-color-scheme: dark)").matches;
	});

	const SunIcon = ({ className = "w-6 h-6", color = "white" }) => (
		<svg
			className={className}
			fill="none"
			viewBox="0 0 24 24"
			stroke={color}
			strokeWidth="2"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M12 3v1m0 16v1m8.66-9H21m-17 0H3m15.364 6.364l-.707.707M6.343 6.343l-.707.707m12.728 0l.707.707M6.343 17.657l.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
			/>
		</svg>
	);

	const MoonIcon = ({ className = "w-6 h-6", color = "white" }) => (
		<svg
			className={className}
			fill="none"
			viewBox="0 0 24 24"
			stroke={color}
			strokeWidth="2"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"
			/>
		</svg>
	);

	useEffect(() => {
		if (darkMode) {
			document.documentElement.classList.add("dark");
			localStorage.setItem("theme", "dark");
		} else {
			document.documentElement.classList.remove("dark");
			localStorage.setItem("theme", "light");
		}
	}, [darkMode]);
	useEffect(() => {
		const handleOutsideClick = (e) => {
			if (
				!e.target.closest("#mobile-menu") &&
				!e.target.closest("#hamburger-btn") &&
				!computeRef.current?.contains(e.target) &&
				!storageRef.current?.contains(e.target) &&
				!networkRef.current?.contains(e.target)
			) {
				setMenuOpen(false);
				setComputeDropdownOpen(false);
				setStorageDropdownOpen(false);
				setNetworkDropdownOpen(false);
			}
		};
		document.addEventListener("mousedown", handleOutsideClick);
		return () =>
			document.removeEventListener("mousedown", handleOutsideClick);
	}, []);

	const toggleDropdown = (dropdown) => {
		if (dropdown === "compute") {
			setComputeDropdownOpen((prev) => {
				if (!prev) {
					setStorageDropdownOpen(false);
					setNetworkDropdownOpen(false);
				}
				return !prev;
			});
		} else if (dropdown === "storage") {
			setStorageDropdownOpen((prev) => {
				if (!prev) {
					setComputeDropdownOpen(false);
					setNetworkDropdownOpen(false);
				}
				return !prev;
			});
		} else if (dropdown === "network") {
			setNetworkDropdownOpen((prev) => {
				if (!prev) {
					setComputeDropdownOpen(false);
					setStorageDropdownOpen(false);
				}
				return !prev;
			});
		}
	};

	return (
		<nav className="sticky top-0 z-50 bg-gray-900 text-gray-100 shadow-lg font-sans">
			<div className="container mx-auto flex justify-between items-center px-6 py-4">
				<a
					href="/"
					className="text-3xl font-extrabold tracking-tight text-emerald-400 hover:text-emerald-300 transition-colors"
					aria-label="GreenCloud Home"
				>
					GreenCloud
				</a>

				<button
					id="hamburger-btn"
					aria-label="Toggle menu"
					aria-expanded={menuOpen}
					onClick={() => setMenuOpen(!menuOpen)}
					className="md:hidden focus:outline-none focus:ring-2 focus:ring-emerald-400 rounded"
				>
					{menuOpen ? (
						<svg
							className="w-8 h-8"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					) : (
						<svg
							className="w-8 h-8"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M4 6h16M4 12h16M4 18h16"
							/>
						</svg>
					)}
				</button>

				<ul className="hidden md:flex md:items-center md:space-x-10 text-lg">
					<li>
						<a
							href="/dashboard"
							className="hover:text-emerald-400 transition font-medium"
						>
							Dashboard
						</a>
					</li>

					{/* Compute Dropdown */}
					<li>
						<a
							href="/instances"
							className="hover:text-emerald-400 transition font-medium"
						>
							Computes
						</a>
					</li>

					{/* <li>
						<a
							href="/billing"
							className="hover:text-emerald-400 transition font-medium"
						>
							Billing
						</a>
					</li> */}
					<li>
						<a
							href="/support"
							className="hover:text-emerald-400 transition font-medium"
						>
							Support
						</a>
					</li>
					<li>
						{" "}
						<div className="flex space-x-2 rounded-md transitn cursor-pointer hover:text-emerald-400 transition font-medium">
							<Link
								to="/profile"
								className="flex gap-2 hover:text-emerald-400"
							>
								<span className="hover:text-emerald-400">Account</span>
							</Link>
						</div>
					</li>
					<li>
						<button
							onClick={(e) => {
								e.preventDefault();
								logout();
							}}
							className="flex items-center gap-2 space-x-2 p-2 font-medium rounded-md hover:bg-red-100 dark:text-white dark:hover:bg-red-600 transition">
							<span className="hidden md:inline-block">
								Logout
							</span>
						</button>
					</li>

					<li>
						{" "}
						<button
							onClick={() => setDarkMode(!darkMode)}
							className="p-2 rounded-md bg-gray-700 text-white:!important"
						>
							{darkMode ? <MoonIcon /> : <SunIcon />}
						</button>
					</li>
				</ul>
			</div>

			{menuOpen && (
				<div
					onClick={() => setMenuOpen(false)}
					className="fixed inset-0 bg-black bg-opacity-50 z-30"
					aria-hidden="true"
				/>
			)}
		</nav>
	);
};

export default Navbar;
